import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HardHat, Zap, Check, Rocket, MapPin, Wrench, Sparkles, Phone } from "lucide-react";
import { toast } from "sonner";
import { TRADES, regionsForCountry, COUNTRIES } from "@/constants/data";
import CountiesPicker from "@/components/CountiesPicker";

export default function GetWork() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState("Ireland");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    county: params.get("county") || "Dublin",
    counties: [],
    trade: params.get("trade") || "Electrician",
    availability: "Ongoing",
    referral_code: (params.get("ref") || "").toUpperCase(),
  });

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/quick-worker-signup", form);
      await refresh();
      const matched = data?.matched_employers || 0;
      if (matched > 0) {
        toast.success(
          `You're on the board — ${matched} contractor${matched === 1 ? "" : "s"} just got notified about you.`,
          { duration: 6000 }
        );
      } else {
        toast.success("You're on the board! Contractors can see you now.");
      }
      navigate("/onboarding/matches");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="get-work-page" className="min-h-screen bg-neutral-950">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16 grid lg:grid-cols-[1.1fr_1fr] gap-10">
        {/* LEFT — pitch */}
        <div>
          {stats && (
            <div className="inline-flex items-center gap-3 bg-neutral-900 border border-neutral-800 px-3 py-2 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-neutral-300">
                <span className="text-amber-500 font-bold">{stats.jobs_live}</span> jobs live
                <span className="mx-2 text-neutral-700">·</span>
                Contractors hiring <span className="text-amber-500 font-bold">right now</span>
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            <div className="h-[2px] w-10 bg-amber-500" />
            <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">For tradespeople</span>
          </div>
          <h1 className="display text-5xl md:text-7xl leading-none">
            Get on <span className="text-amber-500">real jobs.</span><br />
            No agency cut.
          </h1>
          <p className="text-neutral-300 text-lg mt-6 max-w-lg">
            Free to sign up. Contractors across Ireland &amp; UK ring you direct. You set your own day rate.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-lg">
            {[
              ["Direct contact", "Contractors ring your phone — no middleman"],
              ["Keep your rate", "You set the price. Nobody takes a cut."],
              ["Ireland &amp; UK", "All 26 IE counties + 27 UK regions — one board"],
              ["First month €4", "Code LAUNCH50 · full Pro access"],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-3 items-start">
                <Check className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={3} />
                <div>
                  <div className="font-bold text-sm">{t}</div>
                  <div className="text-neutral-400 text-xs mt-0.5">{d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-3 text-neutral-500 text-xs uppercase tracking-widest">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span>4.9 avg · 200+ crews already on the board</span>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 h-fit sticky top-4">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-5 h-5 text-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-amber-500 font-bold">On the board in 60 seconds</span>
          </div>
          <h2 className="display text-3xl mb-6">Sign up. Start earning.</h2>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Full name</label>
              <input data-testid="qs-name" required value={form.name} onChange={setF("name")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="Paddy Murphy" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Country</label>
              <div className="flex gap-2">
                {COUNTRIES.map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`qs-country-${c === "Ireland" ? "ie" : "uk"}`}
                    onClick={() => {
                      setCountry(c);
                      const first = regionsForCountry(c)[0];
                      setForm((f) => ({ ...f, county: first, counties: [] }));
                    }}
                    className={`flex-1 px-3 py-3 border text-xs uppercase tracking-widest transition-colors ${
                      country === c
                        ? "bg-amber-500 text-neutral-950 border-amber-500 font-bold"
                        : "bg-neutral-950 border-neutral-800 text-neutral-400 hover:border-amber-500"
                    }`}
                  >
                    {c === "Ireland" ? "🇮🇪 Ireland" : "🇬🇧 UK"}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Trade</label>
                <select data-testid="qs-trade" required value={form.trade} onChange={setF("trade")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
                  {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">{country === "Ireland" ? "County" : "Region"}</label>
                <select data-testid="qs-county" required value={form.county} onChange={setF("county")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
                  {regionsForCountry(country).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">
                Also willing to work in <span className="text-neutral-600 font-normal normal-case">(optional — tick any county you'd travel to)</span>
              </label>
              <CountiesPicker
                primary={form.county}
                value={form.counties}
                onChange={(next) => setForm((f) => ({ ...f, counties: next }))}
              />
              {form.counties.length > 0 && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-500">
                  {form.counties.length} extra {form.counties.length === 1 ? "county" : "counties"} — more jobs will find you
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Mobile <span className="text-neutral-600 font-normal normal-case">(so contractors can ring you)</span></label>
              <input data-testid="qs-phone" required value={form.phone} onChange={setF("phone")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="083 123 4567" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Availability</label>
              <select data-testid="qs-availability" required value={form.availability} onChange={setF("availability")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
                <option>Ongoing</option>
                <option>Weekend</option>
                <option>Week</option>
                <option>Month</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Email</label>
              <input data-testid="qs-email" required type="email" value={form.email} onChange={setF("email")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Password (min 6)</label>
              <input data-testid="qs-password" required type="password" minLength={6} value={form.password} onChange={setF("password")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="••••••••" />
            </div>
            {form.referral_code && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-amber-500 block mb-1.5">Referral code applied ✓</label>
                <input data-testid="qs-ref" readOnly value={form.referral_code} className="w-full bg-neutral-950 border border-amber-500/60 text-amber-400 outline-none px-4 py-3 font-mono tracking-widest" />
              </div>
            )}

            <button
              data-testid="qs-submit"
              type="submit"
              disabled={busy}
              className="w-full py-4 bg-amber-500 text-neutral-950 uppercase tracking-widest text-sm font-bold hover:bg-amber-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Rocket className="w-4 h-4" />
              {busy ? "Putting you on the board…" : "Get on the board — free"}
            </button>
            <div className="text-center text-[10px] uppercase tracking-widest text-neutral-500">
              No card required · Cancel anytime · <Link to="/login" className="text-amber-500 hover:underline">Already have an account?</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
